import { useEffect, useState } from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import { Slider } from "../components/ui/slider";
import { Reveal } from "../lib/anim";
import { TrendingUp, Users, Route, Network, Lock, Sparkles, Fuel } from "lucide-react";

const BASE_RATE = 1.5; // ₹/km — default rate
const UNLOCKED_RATE = 2.0; // ₹/km — after 3+ active referrals
const REF_UNLOCK = 3;
const REF_BONUS_STEP = 3;        // every 3 referrals ...
const REF_BONUS_STEP_PCT = 0.05; // ... unlocks another 5% bonus on the user's OWN km
const REF_NETWORK_PCT = 0.05;    // fixed 5% of network km once unlocked
const FUEL_EXPENSE_PER_REF = 500; // ₹ per active referral (flat fuel reimbursement)
const MILESTONE_STEP = 7;          // every 7 active referrals ...
const MILESTONE_REWARD = 3500;     // ... unlocks a flat ₹3,500 bonus (stacks)
const PETROL_PRICE = 105; // ₹/litre — indicative India average, used for the ROI chip

function AnimatedRupee({ value }) {
  const spring = useSpring(value, { stiffness: 90, damping: 18 });
  const display = useTransform(spring, (v) => Math.round(v).toLocaleString("en-IN"));
  useEffect(() => {
    spring.set(value);
  }, [value, spring]);
  return (
    <span className="font-mono-gx">
      &#8377;<motion.span data-testid="calc-total-value">{display}</motion.span>
    </span>
  );
}

export const Calculator = () => {
  const [km, setKm] = useState(3000);
  const [refs, setRefs] = useState(7);
  const [refsKm, setRefsKm] = useState(21000);

  const bonusUnlocked = refs >= REF_UNLOCK;
  const rate = bonusUnlocked ? UNLOCKED_RATE : BASE_RATE;
  const ownBonusPct = Math.floor(refs / REF_BONUS_STEP) * REF_BONUS_STEP_PCT; // 0, 5, 10, 15, ...
  const networkBonusPct = bonusUnlocked ? REF_NETWORK_PCT : 0;                 // fixed 5% once unlocked
  const baseEarn = km * rate;
  const refBonusOwn = km * ownBonusPct * rate;
  const refBonusNetwork = refsKm * networkBonusPct * rate;
  const fuelExpense = refs * FUEL_EXPENSE_PER_REF;
  const milestones = Math.floor(refs / MILESTONE_STEP); // 0, 1, 2, 3 at 0/7/14/21
  const milestoneReward = milestones * MILESTONE_REWARD;
  const total = baseEarn + refBonusOwn + refBonusNetwork + fuelExpense + milestoneReward;
  const _remaining = refs % REF_BONUS_STEP;
  const nextTier = _remaining === 0 ? REF_BONUS_STEP : REF_BONUS_STEP - _remaining;
  const nextTierPct = ((Math.floor(refs / REF_BONUS_STEP) + 1) * REF_BONUS_STEP_PCT * 100).toFixed(0);

  const kmNeededToUnlock = Math.max(0, REF_UNLOCK - refs);

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      className="relative py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12"
    >
      <Reveal className="text-center max-w-2xl mx-auto">
        <p className="font-mono-gx text-xs uppercase tracking-[0.25em] text-[#4CAF50] mb-4">
          / Earnings Simulator
        </p>
        <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.1]">
          See your monthly <span className="gx-text-gradient">petrol money.</span>
        </h2>
        <p className="mt-5 text-slate-400">
          Drag the sliders. Watch your INR payout update in real time — paid straight to your bank account.
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-14 grid lg:grid-cols-[1.1fr_0.9fr] gap-6 lg:gap-8 items-stretch">
          {/* Controls */}
          <div className="gx-glass rounded-3xl border border-white/15 p-8 md:p-10">
            {/* User km */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <Route size={16} className="text-[#00B4D8]" /> Kilometres driven / month
                </label>
                <span data-testid="calc-km-value" className="font-mono-gx text-lg font-bold text-white">
                  {km.toLocaleString("en-IN")} km
                </span>
              </div>
              <Slider
                data-testid="calc-km-slider"
                value={[km]}
                min={0}
                max={8000}
                step={100}
                onValueChange={(v) => setKm(v[0])}
                className="[&_[role=slider]]:border-[#00B4D8] [&_[role=slider]]:bg-[#00B4D8] [&_.bg-primary]:bg-[#00B4D8]"
              />
            </div>

            {/* Active referrals */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <Users size={16} className="text-[#4CAF50]" /> Active referrals
                </label>
                <span data-testid="calc-refs-value" className="font-mono-gx text-lg font-bold text-white">
                  {refs}
                </span>
              </div>
              <Slider
                data-testid="calc-refs-slider"
                value={[refs]}
                min={0}
                max={21}
                step={1}
                onValueChange={(v) => setRefs(v[0])}
                className="[&_[role=slider]]:border-[#4CAF50] [&_[role=slider]]:bg-[#4CAF50] [&_.bg-primary]:bg-[#4CAF50]"
              />
            </div>

            {/* Referral network km */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <Network size={16} className="text-[#4CAF50]" /> Kilometres driven / month by active referrals
                </label>
                <span data-testid="calc-refs-km-value" className="font-mono-gx text-lg font-bold text-white">
                  {refsKm.toLocaleString("en-IN")} km
                </span>
              </div>
              <Slider
                data-testid="calc-refs-km-slider"
                value={[refsKm]}
                min={0}
                max={60000}
                step={500}
                onValueChange={(v) => setRefsKm(v[0])}
                className="[&_[role=slider]]:border-[#4CAF50] [&_[role=slider]]:bg-[#4CAF50] [&_.bg-primary]:bg-[#4CAF50]"
              />
              {!bonusUnlocked ? (
                <p data-testid="calc-lock-hint" className="mt-3 inline-flex items-center gap-1.5 text-xs text-amber-300/85 font-mono-gx">
                  <Lock size={12} /> Refer {kmNeededToUnlock} more {kmNeededToUnlock === 1 ? "friend" : "friends"} to unlock ₹2/km + 5% referral bonus + fuel expense benefits.
                </p>
              ) : (
                <p data-testid="calc-unlocked-hint" className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#4CAF50] font-mono-gx">
                  <Sparkles size={12} /> Own bonus {(ownBonusPct * 100).toFixed(0)}% · network bonus 5% · {nextTier} more {nextTier === 1 ? "referral" : "referrals"} to reach {nextTierPct}% own.
                </p>
              )}
            </div>
          </div>

          {/* Payout readout */}
          <div className="relative overflow-hidden rounded-3xl border border-[#4CAF50]/30 bg-[#112240] p-8 md:p-10 flex flex-col justify-between gx-green-glow">
            <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#4CAF50]/20 blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-2 text-[#4CAF50] mb-3">
                <TrendingUp size={18} />
                <span className="font-mono-gx text-xs uppercase tracking-[0.2em]">
                  Estimated Monthly Payout
                </span>
              </div>
              <div className="text-5xl md:text-6xl font-black tracking-tight text-white">
                <AnimatedRupee value={total} />
              </div>
              <p className="text-xs text-slate-400 mt-2">Paid 100% in INR to your bank account.</p>
              {total > 0 && (
                <div
                  data-testid="calc-fuel-chip"
                  className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#00B4D8]/40 bg-[#00B4D8]/10 pl-3 pr-4 py-1.5"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#00B4D8]/20">
                    <Fuel size={13} className="text-[#00B4D8]" />
                  </span>
                  <span className="text-sm text-slate-100 font-mono-gx">
                    <span className="text-slate-400">=</span>{" "}
                    <span data-testid="calc-fuel-litres" className="font-bold text-[#00B4D8]">
                      {(total / PETROL_PRICE).toLocaleString("en-IN", {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      })}
                    </span>{" "}
                    litres of petrol
                  </span>
                </div>
              )}
            </div>

            <div className="relative mt-8 space-y-3 border-t border-white/10 pt-6">
              <Row label={`Driving (${km.toLocaleString("en-IN")} km × ₹${rate.toFixed(2)})`} value={baseEarn} testid="calc-row-driving" />
              <Row
                label={`Own referral bonus · ${(ownBonusPct * 100).toFixed(0)}% of your km${ownBonusPct > 0 ? "" : " (locked)"}`}
                value={refBonusOwn}
                accent
                muted={ownBonusPct === 0}
                testid="calc-row-own-bonus"
              />
              <Row
                label={`Network bonus · 5% of ${refsKm.toLocaleString("en-IN")} km${networkBonusPct > 0 ? "" : " (locked)"}`}
                value={refBonusNetwork}
                accent
                muted={networkBonusPct === 0}
                testid="calc-row-network-bonus"
              />
              <Row
                label={`Fuel expense benefit · ${refs} × ₹${FUEL_EXPENSE_PER_REF}${refs > 0 ? "" : " (add a referral)"}`}
                value={fuelExpense}
                accent
                muted={refs === 0}
                testid="calc-row-fuel-expense"
              />
              <Row
                label={`Milestone reward · ${milestones} × ₹${MILESTONE_REWARD.toLocaleString("en-IN")}${milestones > 0 ? "" : ` (unlocks at ${MILESTONE_STEP} refs)`}`}
                value={milestoneReward}
                accent
                muted={milestones === 0}
                testid="calc-row-milestone"
              />
            </div>
          </div>
        </div>
      </Reveal>

      {/* Amit simulation callout */}
      <Reveal delay={0.15}>
        <div
          data-testid="amit-simulation"
          className="mt-8 rounded-3xl border border-[#00B4D8]/25 bg-gradient-to-r from-[#00B4D8]/10 to-transparent p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-8"
        >
          <div className="font-mono-gx text-xs uppercase tracking-[0.2em] text-[#00B4D8] shrink-0">
            The Amit Simulation
          </div>
          <p className="text-slate-200 text-sm md:text-base flex-1">
            <span className="font-semibold text-white">3,000 km driven</span> +{" "}
            <span className="font-semibold text-white">7 active referrals</span> mapping{" "}
            <span className="font-semibold text-white">21,000 km</span> ={" "}
            <span className="font-bold text-[#4CAF50]">
              &#8377;{Math.round(
                3000 * UNLOCKED_RATE
                + 3000 * UNLOCKED_RATE * (Math.floor(7 / REF_BONUS_STEP) * REF_BONUS_STEP_PCT)
                + 21000 * UNLOCKED_RATE * REF_NETWORK_PCT
                + 7 * FUEL_EXPENSE_PER_REF
                + Math.floor(7 / MILESTONE_STEP) * MILESTONE_REWARD
              ).toLocaleString("en-IN")}
            </span>{" "}
            total monthly payout, directly to your bank account.
          </p>
          <button
            data-testid="amit-load-btn"
            onClick={() => {
              setKm(3000);
              setRefs(7);
              setRefsKm(21000);
            }}
            className="rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold hover:border-[#00B4D8] hover:text-[#00B4D8] transition-colors duration-200 shrink-0"
          >
            Load Amit&apos;s numbers
          </button>
        </div>
      </Reveal>
    </section>
  );
};

const Row = ({ label, value, accent, muted, testid }) => (
  <div className="flex items-center justify-between text-sm" data-testid={testid}>
    <span className={muted ? "text-slate-500" : "text-slate-400"}>{label}</span>
    <span
      className={`font-mono-gx font-semibold ${
        muted ? "text-slate-600" : accent ? "text-[#4CAF50]" : "text-white"
      }`}
    >
      &#8377;{Math.round(value).toLocaleString("en-IN")}
    </span>
  </div>
);
